import { For, If, createEffect, location, onMount, reactive } from 'cdui-js';
import { nextProcess, pState } from '../store';
import { showActionSheet } from '../../../components/ActionSheet';
import i18n from '../../../../../i18n';
import { replaceTemplate } from '@china-difi/lib/template';
import type { AgreementSceneDocItem } from '../../../../../api/agreement-doc';
import { agreementLinkLabel } from '../../../../../lib/agreementLinkLabel';
import { OPEN_ACCOUNT_TRANSFER, formatTransferAmount } from '../../../utils/open-account-transfer';
import { notifyOpenProgressReady } from '../notifyOpenProgressReady';
import {
  consumeOpenAccountReopenTip,
  routeToOpenAccountDocs,
} from '../openAccountDocsNav';

const SCENE_OPEN_ACCOUNT_TIP = 'SCENE_OPEN_ACCOUNT_TIP';
/** 客户个人资料保障声明（弹窗外单独链接，不进开户文件列表） */
const DOC_DATA_PROTECTION = 'AGR002';

export const PreparePage = () => {
  const list = [
    {
      title: () => i18n.OpenProgress.Prepare.StepInfoTitle,
      desc: () => i18n.OpenProgress.Prepare.StepInfoDesc,
    },
    {
      title: () => i18n.OpenProgress.Prepare.StepRiskTitle,
      desc: () => '',
    },
    {
      title: () =>
        replaceTemplate(i18n.OpenProgress.Prepare.StepTransferTitle, {
          name: i18n.OpenProgress.Common.DefaultCompanyName,
        }),
      desc: () => (
        <div>
          {i18n.OpenProgress.Prepare.StepTransferDescBefore}
          <span class="color-D">{formatTransferAmount(OPEN_ACCOUNT_TRANSFER.minHkd)}</span>
          {i18n.OpenProgress.Prepare.StepTransferDescHkd}
          <span class="color-D">{formatTransferAmount(OPEN_ACCOUNT_TRANSFER.minUsd)}</span>
          {i18n.OpenProgress.Prepare.StepTransferDescUsd}
        </div>
      ),
    },
  ];

  const state = reactive({
    dataProtection: null as AgreementSceneDocItem | null,
  });

  let actionSheetIns: { close: (options?: { skipHistoryBack?: boolean }) => void } | null = null;
  /** 关层动画结束后再跳开户文件页，避免与 overlay history.back 抢路由 */
  let pendingOpenAccountDocs = false;
  let reopenTipTimer: ReturnType<typeof setTimeout> | null = null;

  const openDoc = (doc: AgreementSceneDocItem | null) => {
    // 关层不 history.back，避免与后续打开协议抢历史
    pendingOpenAccountDocs = false;
    actionSheetIns?.close({ skipHistoryBack: true });
    void import('../../../../../lib/agreement-doc-view').then(({ openAgreementDoc }) => {
      openAgreementDoc(doc);
    });
  };

  /** 开户文件 → 独立列表页（关提示层后再跳） */
  const openAccountDocs = () => {
    pendingOpenAccountDocs = true;
    actionSheetIns?.close();
  };

  const next = () => {
    // 重弹时若仍有残留引用，先清再建
    if (actionSheetIns) {
      try {
        actionSheetIns.close({ skipHistoryBack: true });
      } catch {
        /* ignore */
      }
      actionSheetIns = null;
    }
    pendingOpenAccountDocs = false;
    actionSheetIns = showActionSheet({
      title: () => <div class="font-bold">{i18n.OpenProgress.Prepare.SheetTitle}</div>,
      contentClass: 'open-progress-tip-sheet',
      onClose: () => {
        actionSheetIns = null;
        if (!pendingOpenAccountDocs) return;
        pendingOpenAccountDocs = false;
        routeToOpenAccountDocs();
      },
      content: () => (
        <div class="font-s-S" style={{ 'line-height': '22px' }}>
          <div class="color">{i18n.OpenProgress.Prepare.SheetIntro}</div>
          <div class="flex margin-t-XS">
            <div style={{ width: '20px' }}>1.</div>
            <div class="flex-auto">
              <span class="agreement-link" onclick={() => openDoc(state.dataProtection)}>
                {agreementLinkLabel(state.dataProtection?.name, i18n.OpenProgress.Risk.Sign.DocDataProtection)}
              </span>
              、
              <span class="agreement-link" onclick={() => openAccountDocs()}>
                {i18n.OpenProgress.Prepare.OpenAccountDocsTitle}
              </span>
              ;
            </div>
          </div>
          <div class="flex margin-t-XS">
            <div style={{ width: '20px' }}>2.</div>
            <div class="flex-auto">
              {replaceTemplate(i18n.OpenProgress.Prepare.SheetItem2, {
                company: i18n.OpenProgress.Common.DefaultCompanyHk,
              })}
            </div>
          </div>
          <div class="flex margin-t-XS">
            <div style={{ width: '20px' }}>3.</div>
            <div class="flex-auto">
              {replaceTemplate(i18n.OpenProgress.Prepare.SheetItem3, {
                company: i18n.OpenProgress.Common.DefaultCompanyIntl,
              })}
            </div>
          </div>
          <div class="flex margin-t-XS">
            <div style={{ width: '20px' }}>4.</div>
            <div class="flex-auto">{i18n.OpenProgress.Prepare.SheetItem4}</div>
          </div>

          <button
            class="primary-btn btn-rounded width-100 margin-t"
            onclick={() => {
              pendingOpenAccountDocs = false;
              actionSheetIns?.close();
              nextProcess();
            }}
          >
            {i18n.OpenProgress.Common.Agree}
          </button>
        </div>
      ),
    });
  };

  /** 从开户文件返回后重弹开户提示 */
  const scheduleReopenTipIfNeeded = () => {
    if (!consumeOpenAccountReopenTip()) return;
    if (reopenTipTimer != null) clearTimeout(reopenTipTimer);
    reopenTipTimer = setTimeout(() => {
      reopenTipTimer = null;
      next();
    }, 80);
  };

  onMount(() => {
    notifyOpenProgressReady(1);
    // 协议 PDF 延后动态加载，不挡准备页骨架（online 流程已在同包内）
    void (async () => {
      try {
        const { loadAgreementDocsByCodesWithPreload } = await import(
          '../../../../../lib/agreement-pdf-cache'
        );
        const { docMap } = await loadAgreementDocsByCodesWithPreload({
          sceneCode: SCENE_OPEN_ACCOUNT_TIP,
          docCodes: [DOC_DATA_PROTECTION],
        });
        state.dataProtection = docMap[DOC_DATA_PROTECTION] ?? null;
      } catch {
        // 接口失败时保留 i18n 兜底文案，点击时 toast
      }
    })();

    scheduleReopenTipIfNeeded();
  });

  // 路由回到开户准备步时再试一次（补 onMount 未吃到标记的情况）
  createEffect(() => {
    void pState.current;
    void location.path;
    if (pState.current !== 1) return;
    if (!String(location.path || '').startsWith('/open-progress')) return;
    scheduleReopenTipIfNeeded();
  });

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="color-D font-s-L font-bold" style={{ 'margin-bottom': '32px' }}>
        {i18n.OpenProgress.Prepare.OnlineTransferTitle}
      </div>
      <div>
        <For each={list}>
          {(item, index) => (
            <div class="flex padding-b-XL align-start">
              <If when={index() < 2}>
                <svg
                  style={{ position: 'relative', left: '11px', top: '24px', 'flex-shrink': 0 }}
                  width="1"
                  height="54"
                  viewBox="0 0 1 54"
                  stroke="none"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="0.5"
                    y1="0.5"
                    x2="0.499998"
                    y2="53"
                    style={{ stroke: 'var(--border-1)' }}
                    stroke-linecap="round"
                    stroke-dasharray="2 4"
                  />
                </svg>
              </If>

              <div
                class="color-D bg-c-XD flex align-center justify-center flex-shrink-0"
                style={{ 'border-radius': '50%', width: '20px', height: '20px', 'margin-top': '2px' }}
              >
                {index() + 1}
              </div>
              {/* border-l border-l-s-dashed  */}
              <div class="padding-l open-progress-step-content">
                <div class="color-D font-s margin-b-XS font-bold">{item.title()}</div>
                <If when={item.desc?.()}>
                  <div class="font-s-S open-progress-step-desc">{item.desc?.()}</div>
                </If>
              </div>
            </div>
          )}
        </For>
      </div>

      <button
        class="primary-btn btn-rounded"
        style={{ height: '50px', width: '100%', 'margin-top': '44px' }}
        onclick={() => next()}
      >
        {i18n.OpenProgress.Common.Next}
      </button>

      {/* <div class="margin-t flex justify-center link-color" onclick={() => lastProcess()}>
        {i18n.OpenProgress.Prepare.OtherMethods}
      </div> */}
    </div>
  );
};
